import { useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { authApi } from '@/api/authApi'
import { safeNext, startSso } from '@/lib/sso'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'

/**
 * `/sso` — the hospital portal entry (`/sso?idp=<name>`) and the IdP picker the
 * backend sends to (`?choose=1`) when it cannot pick a provider itself.
 */
export function SsoStartPage() {
  const [params] = useSearchParams()
  const idp = params.get('idp') ?? undefined
  const choose = params.get('choose') === '1'
  const next = safeNext(params.get('next'))

  const config = useQuery({ queryKey: ['sso-config'], queryFn: authApi.ssoConfig, enabled: !idp })
  const providers = config.data?.providers ?? []
  const only = !choose && providers.length === 1 ? providers[0].name : undefined

  useEffect(() => {
    if (idp) startSso({ idp, next })
    else if (only) startSso({ idp: only, next })
  }, [idp, only, next])

  const redirecting = !!idp || !!only || config.isLoading

  return (
    <div className='flex min-h-screen items-center justify-center bg-muted/30 px-4'>
      <Card className='w-full max-w-sm'>
        <CardHeader className='items-center text-center'>
          <img
            src='/images/title21-lockup.png'
            alt='Title21'
            width={1322}
            height={336}
            className='mb-2 h-auto w-72'
          />
          <CardTitle className='text-xl'>Single sign-on</CardTitle>
          <CardDescription>
            {redirecting ? 'Redirecting to your organization…' : 'Choose your organization'}
          </CardDescription>
        </CardHeader>
        <CardContent className='flex flex-col gap-3'>
          {redirecting ? (
            <Loader2 className='mx-auto h-6 w-6 animate-spin text-muted-foreground' />
          ) : (
            <>
              {config.isError && (
                <p className='text-sm text-destructive' role='alert'>
                  Could not load sign-in options.
                </p>
              )}
              {config.isSuccess && providers.length === 0 && (
                <p className='text-sm text-muted-foreground'>Single sign-on is not enabled.</p>
              )}
              {providers.map((p) => (
                <Button key={p.name} variant='outline' onClick={() => startSso({ idp: p.name, next })}>
                  {p.displayName}
                </Button>
              ))}
              <Link to='/login' className='text-center text-sm text-muted-foreground hover:underline'>
                or enter your email
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
